import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, NavLink, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  LayoutDashboard, FolderKanban, FileCheck2, ScrollText, Image as ImageIcon,
  Sparkles, BookOpen, LogOut, Search, Send, Upload, ShieldCheck, AlertTriangle,
  CheckCircle2, ChevronRight, Building2, Hash, BarChart3, MessageSquareText, Loader2, X,
  Inbox, Link as LinkIcon, Menu, LogIn, FileText
} from "lucide-react";
import { API, BACKEND_URL } from "../lib/api";
import { fmtAUD, fmtMoney, fmtBytes } from "../lib/format";
import { FileInspectModal, SourceTreeSortableHeader, SourcePackageTree, VerifiedPackageTree } from "../components/PackageTrees";
import { useAuth } from "../hooks/useAuth";
import { SLAPill } from "../components/SLAPill";
import { Stat } from "../lib/ui";
import { applyLedgerOverrides, LedgerRowOverrideControls, LedgerChecker } from "../pages/LedgerChecker";
import { IngestProgressInline } from "../components/IngestProgressInline";
import { RFIResponseButton, EvidentiaryCETButton, ClaimValueBanner } from "../components/IngestJobAddOns";

export function CETSlipBreakdown({ jobId, dm, hasCet, hasSlip, hideTrigger = false, externalOpen = false, onExternalClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(false);

  const isOpen = open || externalOpen;
  const closeModal = () => {
    setOpen(false);
    if (onExternalClose) onExternalClose();
  };

  useEffect(() => {
    if (!hasCet && !hasSlip && !externalOpen) return;
    let cancelled = false;
    axios.get(`${API}/ingest/jobs/${jobId}/dm-cet-detail/${dm}`)
      .then(r => { if (!cancelled) { setD(r.data); setLoading(false); } })
      .catch(e => {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || e?.message || "Failed to load line-item detail");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [jobId, dm, hasCet, hasSlip, externalOpen]);

  if (!hasCet && !hasSlip && !externalOpen) return null;
  // When triggered externally (from a DM-row dollar value click) we want the
  // modal scaffold to appear immediately even before the fetch resolves —
  // showing a loading state INSIDE the modal rather than a tiny grey line
  // somewhere off-page (which previously made the click look unresponsive).
  if (externalOpen && (loading || !d)) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
        onClick={closeModal}
        data-testid={`dm-${dm}-cet-modal`}
      >
        <div
          className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6 flex items-center gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <Loader2 className="w-5 h-5 animate-spin text-slate-500"/>
          <div className="text-sm text-slate-700">
            {err ? (
              <div className="space-y-2">
                <div className="text-red-600">{err}</div>
                {err.includes("cleaned up") && (
                  <Link to="/inbox" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-700">
                    <Upload className="w-3.5 h-3.5"/> Go to Inbox to re-ingest
                  </Link>
                )}
              </div>
            ) : (
              <>Loading CET workings for <span className="font-mono font-semibold">{dm}</span>…</>
            )}
          </div>
          <button onClick={closeModal} className="ml-auto p-1.5 hover:bg-slate-100 rounded shrink-0">
            <X className="w-4 h-4"/>
          </button>
        </div>
      </div>
    );
  }
  if (loading) return <div className="text-[11px] text-slate-500 italic">Loading cost build-up…</div>;
  if (err) return <div className="text-[11px] text-red-600">Line-item error: {err}</div>;
  if (!d) return null;

  const cet = d.cet;
  const slip = d.slip;
  const cetLines = cet?.lines || [];
  const slipLines = slip?.lines || [];
  const realCetLines = cetLines.filter(l => !l.is_parent);
  const cetVisible = showAll ? cetLines : realCetLines;
  const slipVisible = showAll ? slipLines : slipLines.filter(l => (l.cost || 0) > 0);

  // Pick the most relevant project value to surface: prefer CET P50 outturn,
  // fall back to CET direct, then slip pricing.
  const headlineValue = cet?.outturn_p50 ?? cet?.direct_total ?? slip?.pricing_estimate ?? null;
  const headlineLabel = cet?.outturn_p50 != null ? "CET P50 outturn"
    : cet?.direct_total != null ? "CET direct cost"
    : "Slip pricing estimate";

  return (
    <>
      {!hideTrigger && (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(true); } }}
        className="w-full text-left border border-slate-200 rounded p-2.5 bg-white hover:bg-slate-50 hover:border-slate-400 transition flex items-center gap-3 cursor-pointer"
        data-testid={`dm-${dm}-cet-trigger`}
        title="Click to view full CET workings"
      >
        <BarChart3 className="w-4 h-4 text-slate-500 shrink-0"/>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-slate-500 select-none">Project value · click for full CET workings</div>
          <div
            className="font-mono font-bold text-slate-800 text-[15px] select-text cursor-text"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            title="Drag to select / copy"
          >{fmtMoney(headlineValue)}</div>
          <div className="text-[10.5px] text-slate-500 select-text" onMouseDown={(e) => e.stopPropagation()}>
            {headlineLabel}
            {cet?.outturn_p90 != null && <span className="ml-2">· P90 risk envelope {fmtMoney(cet.outturn_p90)}</span>}
            {slip?.pricing_estimate != null && cet?.direct_total != null && (
              <span className="ml-2">· Slip estimate {fmtMoney(slip.pricing_estimate)}</span>
            )}
          </div>
        </div>
        {cet?.cost_bands && Object.values(cet.cost_bands).some(v => v.status === "above") && (
          <span className="text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 shrink-0 select-none">flags</span>
        )}
        <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0 select-none"/>
      </div>
      )}

      {isOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid={`dm-${dm}-cet-modal`}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-3 shrink-0">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500">CET workings — {dm}</div>
                <h3 className="font-heading text-base font-semibold truncate" title={cet?.filename || ""}>{cet?.filename || slip?.filename || dm}</h3>
                <div className="text-[10.5px] text-slate-500">{cet?.lga || ""}{cet?.project_fy ? ` · FY ${cet.project_fy}` : ""}</div>
              </div>
              <button onClick={closeModal} className="p-1.5 hover:bg-slate-100 rounded shrink-0" data-testid={`dm-${dm}-cet-close`}>
                <X className="w-4 h-4"/>
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-auto px-5 py-4 space-y-4" data-testid={`dm-${dm}-line-breakdown`}>
              {/* Headline comparison strip */}
              {d.comparison && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">CET direct</div>
                    <div className="font-mono font-semibold text-slate-800">{fmtMoney(d.comparison.cet_direct_total)}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-emerald-700">CET P50 outturn · eligible</div>
                    <div className="font-mono font-semibold text-emerald-800">{fmtMoney(cet?.outturn_p50)}</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">Slip pricing estimate</div>
                    <div className="font-mono font-semibold text-slate-800">{fmtMoney(d.comparison.slip_pricing)}</div>
                  </div>
                  <div className={`border rounded p-2 ${
                    Math.abs(d.comparison.delta_aud) < Math.max(d.comparison.slip_pricing * 0.05, 10000)
                      ? "bg-emerald-50 border-emerald-200"
                      : "bg-amber-50 border-amber-200"
                  }`}>
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">CET − Slip</div>
                    <div className="font-mono font-semibold text-slate-800">{fmtMoney(d.comparison.delta_aud)}</div>
                  </div>
                </div>
              )}
              {d.comparison?.notes?.length > 0 && (
                <ul className="text-[11px] text-slate-700 space-y-0.5 list-disc pl-4">
                  {d.comparison.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              )}

              {/* Toggle for empty rows */}
              <div className="flex items-center justify-end">
                <button
                  type="button"
                  className="text-[10px] uppercase tracking-wider text-slate-500 hover:text-slate-900 underline-offset-2 hover:underline"
                  onClick={() => setShowAll(s => !s)}
                >
                  {showAll ? "Hide zero-rate rows" : "Show all rows"}
                </button>
              </div>
        {/* CET workbook lines — Submitted (council) vs Verified (CET v2.1 schedule) */}
        {cet && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              CET workbook · {realCetLines.length} costed line{realCetLines.length === 1 ? "" : "s"}
              {cetLines.length > realCetLines.length && (
                <span className="ml-1 normal-case font-normal text-slate-400">({cetLines.length - realCetLines.length} parent rows)</span>
              )}
              {cet.project_fy && <span className="ml-2 normal-case font-normal text-slate-400">FY {String(cet.project_fy)}</span>}
            </div>
            <div className="font-mono text-[10.5px] text-slate-500 truncate mb-1" title={cet.filename}>{cet.filename}</div>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-[10.5px]">
                <thead>
                  <tr className="bg-slate-200 text-left text-slate-700">
                    <th className="p-1.5 font-semibold" rowSpan={2}>Ref / treatment</th>
                    <th className="p-1.5 font-semibold text-right" rowSpan={2}>Qty</th>
                    <th className="p-1.5 font-semibold" rowSpan={2}>Unit</th>
                    <th className="p-1.5 font-semibold text-center border-l border-slate-300" colSpan={2}>Submitted (council)</th>
                    <th className="p-1.5 font-semibold text-center border-l border-slate-300" colSpan={2}>Verified (CET v2.1)</th>
                    <th className="p-1.5 font-semibold text-center border-l border-slate-300" colSpan={2}>Variance</th>
                  </tr>
                  <tr className="bg-slate-100 text-left text-slate-600">
                    <th className="p-1.5 font-semibold text-right border-l border-slate-300">Rate</th>
                    <th className="p-1.5 font-semibold text-right">Direct</th>
                    <th className="p-1.5 font-semibold text-right border-l border-slate-300">Rate</th>
                    <th className="p-1.5 font-semibold text-right">Direct</th>
                    <th className="p-1.5 font-semibold text-right border-l border-slate-300">%</th>
                    <th className="p-1.5 font-semibold text-right">$ effect</th>
                  </tr>
                </thead>
                <tbody>
                  {cetVisible.map((l, i) => {
                    const v = l.verification || {};
                    const status = v.status || "—";
                    const statusTone = status === "match" ? "bg-emerald-50"
                      : status === "above" ? "bg-red-50"
                      : status === "below" ? "bg-amber-50"
                      : status === "lump_sum" ? "bg-blue-50"
                      : l.is_parent ? "bg-slate-50 italic text-slate-500"
                      : "";
                    return (
                      <tr
                        key={`cet-${l.row}-${i}`}
                        className={`border-t border-slate-100 ${statusTone}`}
                        title={v.decision_hint || ""}
                      >
                        <td className="p-1.5">
                          <div className="font-mono text-[10px] text-slate-500">{l.ref || ""}</div>
                          <div className="truncate max-w-[220px]" title={l.treatment}>{l.treatment}</div>
                          {l.description && <div className="text-[9.5px] text-slate-500 truncate max-w-[220px]" title={l.description}>{l.description}</div>}
                        </td>
                        <td className="p-1.5 font-mono text-right whitespace-nowrap">{l.qty != null ? Number(l.qty).toLocaleString() : "—"}</td>
                        <td className="p-1.5 whitespace-nowrap">{l.unit || "—"}</td>
                        <td className="p-1.5 font-mono text-right border-l border-slate-200 whitespace-nowrap">{l.direct_rate != null ? fmtMoney(l.direct_rate) : "—"}</td>
                        <td className="p-1.5 font-mono text-right whitespace-nowrap">{fmtMoney(l.direct_total)}</td>
                        <td className="p-1.5 font-mono text-right border-l border-slate-200 whitespace-nowrap">{v.ref_rate != null ? fmtMoney(v.ref_rate) : <span className="text-slate-400">—</span>}</td>
                        <td className="p-1.5 font-mono text-right whitespace-nowrap">{v.verified_direct != null ? fmtMoney(v.verified_direct) : <span className="text-slate-400">—</span>}</td>
                        <td className="p-1.5 font-mono text-right border-l border-slate-200 whitespace-nowrap">
                          {v.variance_pct != null
                            ? <span className={v.variance_pct > 5 ? "text-red-700 font-semibold" : v.variance_pct < -5 ? "text-amber-700 font-semibold" : "text-emerald-700"}>{v.variance_pct > 0 ? "+" : ""}{v.variance_pct}%</span>
                            : <span className={`text-[9.5px] uppercase tracking-wider px-1 rounded ${
                                status === "lump_sum" ? "bg-blue-100 text-blue-700"
                                  : status === "unmapped" ? "bg-slate-200 text-slate-600"
                                  : "text-slate-400"
                              }`}>{status === "lump_sum" ? "lump sum" : status === "unmapped" ? "unmapped" : "—"}</span>}
                        </td>
                        <td className="p-1.5 font-mono text-right whitespace-nowrap">
                          {v.delta_direct != null
                            ? <span className={v.delta_direct > 0 ? "text-red-700" : v.delta_direct < 0 ? "text-amber-700" : "text-emerald-700"}>{fmtMoney(v.delta_direct)}</span>
                            : <span className="text-slate-400">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 border-t border-slate-300 font-semibold">
                    <td colSpan={4} className="p-1.5 text-right text-slate-600">Totals</td>
                    <td className="p-1.5 font-mono text-right">{fmtMoney(cet.direct_total)}</td>
                    <td colSpan={2} className="p-1.5 font-mono text-right text-slate-500 text-[10px]">P50 outturn (N26)</td>
                    <td colSpan={2} className="p-1.5 font-mono text-right">{fmtMoney(cet.outturn_p50)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {cet.error && <div className="text-[10.5px] text-red-600 mt-1">{cet.error}</div>}

            {/* Cost-band check (PM / Design / Contingency / Escalation) */}
            {cet.cost_bands && cet.cost_band_thresholds && (
              <div className="mt-3 border border-slate-200 rounded p-2.5 bg-white">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 mb-2">Cost-band check (vs CET v2.1 thresholds)</div>
                <table className="w-full text-[10.5px]">
                  <thead className="bg-slate-100">
                    <tr className="text-left text-slate-600">
                      <th className="p-1.5 font-semibold">Component</th>
                      <th className="p-1.5 font-semibold text-right">Actual %</th>
                      <th className="p-1.5 font-semibold text-right">Allowed band</th>
                      <th className="p-1.5 font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(cet.cost_bands).filter(([k]) => k !== "error").map(([k, v]) => {
                      const label = k.replace(/_/g, " ").replace(" pct of direct", "");
                      const band = v.band || cet.cost_band_thresholds[k];
                      const tone = v.status === "within" ? "bg-emerald-50"
                        : v.status === "above" ? "bg-red-50"
                        : "bg-amber-50";
                      return (
                        <tr key={k} className={`${tone} border-t border-slate-100`}>
                          <td className="p-1.5 capitalize">{label}</td>
                          <td className="p-1.5 font-mono text-right">{v.actual_pct}%</td>
                          <td className="p-1.5 font-mono text-right">{band.min}%–{band.max}%</td>
                          <td className="p-1.5">
                            <span className={`text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded font-semibold ${
                              v.status === "within" ? "bg-emerald-200 text-emerald-800"
                                : v.status === "above" ? "bg-red-200 text-red-800"
                                : "bg-amber-200 text-amber-800"
                            }`}>{v.status}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {cet.cost_bands.error && <div className="text-[10.5px] text-red-600 mt-1">{cet.cost_bands.error}</div>}
              </div>
            )}

            {/* P50 vs P90 — claim policy panel */}
            {(cet.outturn_p50 != null || cet.outturn_p90 != null) && (
              <div className="mt-3 border border-blue-200 rounded p-3 bg-blue-50">
                <div className="flex items-start gap-2 mb-2">
                  <ShieldCheck className="w-4 h-4 text-blue-700 mt-0.5 shrink-0"/>
                  <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold text-blue-900">Why P50, not P90?</div>
                    <div className="text-[11.5px] text-blue-900 mt-0.5 leading-relaxed">
                      The CET computes both percentiles. NSWRA + the Commonwealth fund the <span className="font-semibold">P50 (50th percentile)</span> figure as the eligible DRFA Cat B claim. <span className="font-semibold">P90</span> is a State-level risk envelope, NOT claimable. Pooling the upside risk at program level is policy — over-funding every project at P90 would inflate the reconstruction claim well beyond statistical need.
                      {cet.outturn_p50 && cet.outturn_p90 && (
                        <span> If the council submitted P90 as the claim, raise an RFI to restate at P50.</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  <div className="bg-white border border-emerald-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-emerald-700">P50 outturn · ELIGIBLE</div>
                    <div className="font-mono font-bold text-emerald-800 text-[13px]">{fmtMoney(cet.outturn_p50)}</div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">P90 outturn · risk envelope</div>
                    <div className="font-mono font-bold text-slate-700 text-[13px]">{fmtMoney(cet.outturn_p90)}</div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">P90 − P50 spread</div>
                    <div className="font-mono font-bold text-slate-700 text-[13px]">{fmtMoney(cet.p50_p90_spread_aud)} {cet.p50_p90_spread_pct != null && <span className="text-slate-500 font-normal text-[11px]">({cet.p50_p90_spread_pct > 0 ? "+" : ""}{cet.p50_p90_spread_pct}%)</span>}</div>
                  </div>
                  <div className="bg-white border border-slate-200 rounded p-2">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500">Cross-check vs claimed</div>
                    <div className="text-[10.5px] text-slate-700 leading-tight mt-0.5">
                      {cet.outturn_p50 && cet.outturn_p90
                        ? "Confirm the council's claim figure matches P50 (not P90)."
                        : "P90 not available — confirm CET is the published v2.1 template."}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* P50 escalation workings */}
            {cet.escalation_workings && cet.escalation_workings.fy_headers?.length > 0 && (
              <div className="mt-3 border border-slate-200 rounded p-2.5 bg-white">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700 mb-2">P50 escalation workings</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10.5px]">
                    <thead className="bg-slate-100">
                      <tr className="text-left text-slate-600">
                        <th className="p-1.5 font-semibold">FY</th>
                        {cet.escalation_workings.fy_headers.map((h, i) => (
                          <th key={i} className="p-1.5 font-semibold text-right">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-slate-100">
                        <td className="p-1.5 text-slate-600">Delivery profile</td>
                        {cet.escalation_workings.delivery_profile_pct.map((p, i) => (
                          <td key={i} className="p-1.5 font-mono text-right">{p}%</td>
                        ))}
                      </tr>
                      <tr className="border-t border-slate-100">
                        <td className="p-1.5 text-slate-600">Escalation rate</td>
                        {cet.escalation_workings.escalation_rate_pct.map((p, i) => (
                          <td key={i} className="p-1.5 font-mono text-right">{p}%</td>
                        ))}
                      </tr>
                      <tr className="border-t border-slate-100">
                        <td className="p-1.5 text-slate-600">Annual P50 total</td>
                        {cet.escalation_workings.annual_p50_total.map((p, i) => (
                          <td key={i} className="p-1.5 font-mono text-right">{fmtMoney(p)}</td>
                        ))}
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr className="bg-slate-100 border-t border-slate-300 font-semibold">
                        <td className="p-1.5 text-slate-600">Base est. {fmtMoney(cet.escalation_workings.base_estimate)} + P50 allowance {fmtMoney(cet.escalation_workings.p50_allowance)} + escalation {fmtMoney(cet.escalation_workings.total_escalation)}</td>
                        <td colSpan={cet.escalation_workings.fy_headers.length} className="p-1.5 font-mono text-right">Outturn {fmtMoney(cet.outturn_p50)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}

            {/* Plain-English explainers for the P50 / contingency / escalation concepts */}
            {cet.explainers && (
              <details className="mt-3 border border-slate-200 rounded p-2.5 bg-slate-50/50">
                <summary className="cursor-pointer text-[10px] uppercase tracking-wider font-bold text-slate-700">
                  What do P50, contingency, escalation, PM and design mean? <span className="font-normal text-slate-400 normal-case">(click to expand)</span>
                </summary>
                <dl className="mt-2 text-[11px] text-slate-700 space-y-1.5">
                  {Object.entries(cet.explainers).map(([k, txt]) => (
                    <div key={k}>
                      <dt className="font-semibold capitalize text-slate-800">{k}:</dt>
                      <dd className="text-slate-600">{txt}</dd>
                    </div>
                  ))}
                </dl>
              </details>
            )}
          </div>
        )}

        {/* Slip estimate lines */}
        {slip && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
              Slip construction estimate · {slipVisible.length} line{slipVisible.length === 1 ? "" : "s"}
            </div>
            <div className="font-mono text-[10.5px] text-slate-500 truncate mb-1" title={slip.filename}>{slip.filename}</div>
            <div className="overflow-x-auto border border-slate-200 rounded">
              <table className="w-full text-[10.5px]">
                <thead className="bg-slate-100">
                  <tr className="text-left text-slate-600">
                    <th className="p-1.5 font-semibold">Item</th>
                    <th className="p-1.5 font-semibold">Task</th>
                    <th className="p-1.5 font-semibold text-right">Qty</th>
                    <th className="p-1.5 font-semibold">UoM</th>
                    <th className="p-1.5 font-semibold text-right">Rate</th>
                    <th className="p-1.5 font-semibold text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {slipVisible.map((l, i) => (
                    <tr key={`slip-${l.row}-${i}`} className="border-t border-slate-100">
                      <td className="p-1.5 font-mono whitespace-nowrap">{l.item ? Number(l.item).toFixed(1).replace(/\.0$/, "") : ""}</td>
                      <td className="p-1.5">
                        <div className="truncate max-w-[200px]" title={l.task}>{l.task}</div>
                        {l.comment && <div className="text-[9.5px] text-slate-500 truncate max-w-[200px]" title={l.comment}>{l.comment}</div>}
                      </td>
                      <td className="p-1.5 font-mono text-right">{l.qty != null ? Number(l.qty).toLocaleString() : "—"}</td>
                      <td className="p-1.5">{l.uom || "—"}</td>
                      <td className="p-1.5 font-mono text-right">{l.rate != null ? fmtMoney(l.rate) : "—"}</td>
                      <td className="p-1.5 font-mono text-right">{fmtMoney(l.cost)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t border-slate-300 font-semibold">
                    <td colSpan={5} className="p-1.5 text-right text-slate-600">Pricing estimate (Summary D2)</td>
                    <td className="p-1.5 font-mono text-right">{fmtMoney(slip.pricing_estimate)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {slip.error && <div className="text-[10.5px] text-red-600 mt-1">{slip.error}</div>}
          </div>
        )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function DMEvidenceDrawer({ jobId, dm, expanded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null); // {url, name}

  useEffect(() => {
    if (!expanded || data) return;
    let cancelled = false;
    axios.get(`${API}/ingest/jobs/${jobId}/dm-evidence/${dm}`)
      .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(e => {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || e?.message || "Failed to load evidence");
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [expanded, data, jobId, dm]);

  if (!expanded) return null;

  return (
    <div className="col-span-12 mt-2 -mx-1 px-3 py-3 bg-white border border-slate-200 rounded-md" data-testid={`dm-${dm}-evidence`}>
      {loading && (
        <div className="flex items-center gap-2 text-[12px] text-slate-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin"/> Loading evidence for {dm}…
        </div>
      )}
      {err && <div className="text-[12px] text-red-600">Evidence error: {err}</div>}
      {data && (
        <div className="space-y-3">
          {data.photos_cleaned_up && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Photo files have been pruned from disk (housekeeping). Counts and CET data still available.
            </div>
          )}

          {/* Full line-item breakdown — CET workbook vs Slip Construction Estimate */}
          <CETSlipBreakdown jobId={jobId} dm={dm} hasCet={!!data.cet} hasSlip={!!data.slip_estimate} />

          {/* Photo strips — buckets depend on assessment type:
                Scope assessments      → pre-event + post-event (damage)
                Finalisation assessments → post-event (damage) + completion */}
          {(() => {
            const isScope = (data.portal_type || "").toLowerCase().includes("scope");
            const bucketsToShow = isScope ? ["pre", "damage"] : ["damage", "completion"];
            const labels = isScope
              ? { pre: "Pre-event photos", damage: "Post-event photos" }
              : { damage: "Post-event photos", completion: "Completion photos" };
            return bucketsToShow.map(bucket => {
              const photos = data[bucket] || [];
              const count = data.counts?.[bucket] || 0;
              if (count === 0) {
                return (
                  <div key={bucket} className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    No {labels[bucket].toLowerCase()} found for {dm}.
                  </div>
                );
              }
              return (
                <div key={bucket}>
                  <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500 mb-1">
                    {labels[bucket]}
                    <span className="ml-2 font-mono normal-case tracking-normal text-slate-400">{photos.length}{count > photos.length ? ` of ${count}` : ""}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {photos.map(p => (
                      <button
                        key={`${bucket}-${p.idx}`}
                        type="button"
                        onClick={() => setLightbox({ url: `${BACKEND_URL}${p.url}`, name: p.name })}
                        className="relative group w-20 h-20 rounded overflow-hidden border border-slate-200 bg-slate-100 hover:border-slate-900 transition"
                        data-testid={`dm-${dm}-photo-${bucket}-${p.idx}`}
                        title={p.name}
                      >
                        <img
                          src={`${BACKEND_URL}${p.url}`}
                          alt={p.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              );
            });
          })()}

          {/* If the OPPOSITE bucket has photos (e.g. completion photos in a Scope package),
              surface it as an anomaly so the assessor knows the upload contained the wrong stage. */}
          {(() => {
            const isScope = (data.portal_type || "").toLowerCase().includes("scope");
            const unexpected = isScope
              ? (data.counts?.completion ? ["completion"] : [])
              : (data.counts?.pre ? ["pre"] : []);
            if (!unexpected.length) return null;
            const labels = { pre: "pre-event", completion: "completion" };
            return (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                Unexpected for a {isScope ? "Scope" : "Finalisation"} assessment: {unexpected.map(b => `${data.counts[b]} ${labels[b]}`).join(", ")} photo(s) also present in this DM. Review them in the inbox queue if needed.
              </div>
            );
          })()}

          {/* Flags / needs_review hits */}
          {data.needs_review && data.needs_review.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2.5">
              <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-amber-700 mb-1">Verifier flags · {data.needs_review.length}</div>
              <ul className="text-[11.5px] text-amber-900 space-y-0.5 list-disc pl-4">
                {data.needs_review.map((it, k) => (
                  <li key={k}>{it.kind?.replace(/_/g," ")}: {it.message}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Claim items matched */}
          {data.claim_items && data.claim_items.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded p-2.5">
              <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500 mb-1">Form 306 / claim items mentioning {dm}</div>
              <ul className="text-[11.5px] text-slate-700 space-y-0.5">
                {data.claim_items.slice(0, 5).map((it, k) => (
                  <li key={k} className="font-mono truncate">
                    {(it.asset_name || it.description || "(unnamed)")} {it.value_aud != null ? `· $${Number(it.value_aud).toLocaleString()}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <img src={lightbox.url} alt={lightbox.name} className="max-w-full max-h-full object-contain shadow-2xl" />
          <button
            type="button"
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
          >
            <X className="w-5 h-5"/>
          </button>
        </div>
      )}
    </div>
  );
}

export function DMDecisionsBlock({ job, onChange }) {
  const { user } = useAuth();
  const _isRfiPath = (p) => {
    const s = String(p || "").toLowerCase();
    if (!s) return false;
    // Folder-level cues (cheap, high signal)
    if (/\brfi(?:[\s_-]+response)?\b/.test(s)) return true;
    if (/\b(updated|revised|supplementary)\b.*\bcet\b/.test(s)) return true;
    if (/\b(response[\s_-]+to[\s_-]+rfi|rfi[\s_-]+response)\b/.test(s)) return true;
    // Filename-level cues
    if (/[/_-]update[s]?[\s_-]?\d{6,8}\.xlsx?$/.test(s)) return true;
    if (/[/_-]revised[/_-]/.test(s) || /[/_-]rev[/_-]/.test(s) || /_v\d+\.xlsx?$/.test(s)) return true;
    return false;
  };
  const rows = (job?.form_2_assets && job.form_2_assets.length > 0)
    ? job.form_2_assets.map(a => ({
        dm: `A-${a.asset_id || a.ref}`,
        site: `${a.asset_name || "(unnamed asset)"}${a.asset_id ? ` · ID ${a.asset_id}` : ""}`,
        pricing: a.submitted_erc,
        is_aggregate: false,
        is_asset_row: true,
        asset_id: a.asset_id,
        asset_name: a.asset_name,
        asset_category: a.asset_category,
        asset_sub_category: a.asset_sub_category,
        dm_no: a.dm_no,
        ref: a.ref,
        is_rfi: false,
      }))
    : (job?.slip_estimates_rollup?.rows && job.slip_estimates_rollup.rows.length > 0)
    ? job.slip_estimates_rollup.rows.map(r => ({ dm: r.dm_no || r.dm_no_summary, site: r.site || r.project_name || "", pricing: r.pricing_estimate, is_rfi: _isRfiPath(r.file) }))
    : (job?.cet_rollup?.rows || []).map(r => ({
        dm: r.dm,
        // Aggregate workbooks have no DM#### — surface the friendly project
        // label + a small "+N DMs" hint so the assessor immediately sees
        // which physical damage memos this workbook rolls up.
        site: r.is_aggregate
          ? (r.project || r.dm) + ((r.child_dms || []).length ? ` · covers ${(r.child_dms || []).length} DM${(r.child_dms || []).length === 1 ? "" : "s"}` : "")
          : (r.file || "").split("/").pop().split(" cost-estimation-tool")[0],
        pricing: r.total_erc,
        is_aggregate: r.is_aggregate,
        child_dms: r.child_dms || [],
        is_suspect_stub: r.is_suspect_stub,
        suspect_stub_sibling_max: r.suspect_stub_sibling_max,
        is_rfi: _isRfiPath(r.file),
      }));

  // Split rows into RFI Response (council's later revision) vs Original Submission so the
  // assessor sees the most-recent rate set up top, with the originals collapsed but available.
  const rfiRows = rows.filter(r => r.is_rfi);
  const originalRows = rows.filter(r => !r.is_rfi);
  // Heuristic: are these *actual* DM workbooks or treatment-rate templates (Dungog-style)?
  // If <50% of rows have a DM\d+ pattern, label the section as "Per-treatment CET verification".
  const isAssetMode = !!(job?.form_2_assets && job.form_2_assets.length > 0);
  const dmLikeCount = rows.filter(r => /DM\d+/i.test(String(r.dm || ""))).length;
  const labelMode = isAssetMode ? "asset" :
                    (dmLikeCount >= Math.ceil(rows.length / 2) ? "dm" : "treatment");
  const sectionTitle = labelMode === "asset"
    ? "Per-asset Form 2 verification (one row per physical asset — Refs 1–21)"
    : labelMode === "dm"
    ? "Per-DM Form 2 verification (writes to Form 2 G + H)"
    : "Per-treatment CET verification (one row per treatment workbook)";

  const decisions = job?.dm_decisions || {};
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState({});
  const [autofilling, setAutofilling] = useState({});
  const [expanded, setExpanded] = useState({});
  const [cetModalDM, setCetModalDM] = useState(null);

  const setField = (dm, key, value) => setDraft(d => ({ ...d, [dm]: { ...(d[dm] || decisions[dm] || {}), [key]: value } }));
  const save = async (dm) => {
    setSaving(s => ({ ...s, [dm]: true }));
    try {
      // Attribute the precedent to the signed-in assessor so the institutional
      // memory has who/when. Backend captures one precedent per saved decision.
      const payload = {
        dm,
        ...(draft[dm] || decisions[dm] || {}),
        assessor_email: user?.email || "",
        assessor_name: user?.name || "",
      };
      await axios.post(`${API}/ingest/jobs/${job.id}/dm-decision`, payload);
      setDraft(d => { const n = { ...d }; delete n[dm]; return n; });
      if (onChange) onChange();
    } finally {
      setSaving(s => ({ ...s, [dm]: false }));
    }
  };
  const autoFill = async (dm) => {
    setAutofilling(s => ({ ...s, [dm]: true }));
    try {
      const r = await axios.post(`${API}/ingest/jobs/${job.id}/dm-auto-fill`, { dm });
      const method = r?.data?.method || "";
      if (method) setField(dm, "method", method);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Auto-fill failed";
      setField(dm, "method", `(auto-fill error: ${msg})`);
    } finally {
      setAutofilling(s => ({ ...s, [dm]: false }));
    }
  };
  const toggle = (dm) => setExpanded(e => ({ ...e, [dm]: !e[dm] }));
  const expandAll = () => setExpanded(rows.reduce((acc, r) => ({ ...acc, [r.dm]: true }), {}));
  const collapseAll = () => setExpanded({});

  if (!rows || rows.length === 0) return null;
  const anyExpanded = Object.values(expanded).some(Boolean);

  return (
    <div className="mt-5 border-t border-slate-200 pt-4" data-testid="dm-decisions-block">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-slate-500">{sectionTitle}</div>
        <button
          type="button"
          onClick={anyExpanded ? collapseAll : expandAll}
          className="text-[11px] font-semibold tracking-wider uppercase text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline"
          data-testid="dm-toggle-all-evidence"
        >
          {anyExpanded ? "Collapse all evidence" : "Show all evidence"}
        </button>
      </div>
      {rfiRows.length > 0 && (
        <div className="mb-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-900 text-[10.5px] font-bold tracking-[0.12em] uppercase" data-testid="rfi-banner">
          ⚡ {rfiRows.length} updated workbook{rfiRows.length === 1 ? "" : "s"} from council RFI response · supersede the originals
        </div>
      )}
      <div className="space-y-2">
        {rows.map((r, i) => {
          const dm = r.dm;
          const current = draft[dm] || decisions[dm] || {};
          const verified = current.verified || "";
          const method = current.method || "";
          const dirty = draft[dm] && (
            (draft[dm].verified ?? "") !== (decisions[dm]?.verified ?? "") ||
            (draft[dm].method ?? "") !== (decisions[dm]?.method ?? "")
          );
          const isOpen = !!expanded[dm];
          return (
            <div key={`${i}-${dm}`} data-testid={`dm-row-${dm}`} className="grid grid-cols-12 gap-2 items-start text-sm bg-slate-50 p-3 rounded-md">
              <div className="col-span-12 md:col-span-3 font-mono text-[12px]">
                <button
                  type="button"
                  onClick={() => toggle(dm)}
                  className="flex items-center gap-1 hover:text-slate-900"
                  data-testid={`dm-${dm}-toggle-evidence`}
                  title={isOpen ? "Hide evidence" : "Show evidence for this DM"}
                >
                  <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`}/>
                  <span className="font-bold">{dm}</span>
                </button>
                <div className="text-[11px] text-slate-500 font-sans pl-4">{r.site}</div>
                {r.pricing != null && (
                  <button
                    type="button"
                    onClick={() => setCetModalDM(dm)}
                    data-testid={`dm-${dm}-price-trigger`}
                    title="Click to view full CET workings (submitted vs verified, P50 vs P90, cost bands)"
                    className="text-[11px] text-blue-600 font-mono pl-4 mt-0.5 hover:text-blue-800 hover:underline cursor-pointer text-left"
                  >{fmtAUD(r.pricing)}</button>
                )}
                {r.is_suspect_stub && (
                  <div className="pl-4 mt-1.5" data-testid={`dm-${dm}-suspect-stub`}>
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] tracking-[0.12em] uppercase font-bold border border-amber-200"
                      title={`This workbook totals just ${fmtAUD(r.pricing)} — looks like a draft alongside the real ${r.suspect_stub_sibling_max ? "$"+r.suspect_stub_sibling_max.toLocaleString() : ""} workbook of the same project. Confirm before finalising.`}
                    >
                      <AlertTriangle className="w-2.5 h-2.5"/> Likely draft — confirm
                    </span>
                  </div>
                )}
              </div>
              <div className="col-span-12 md:col-span-2 flex gap-1">
                {["Yes","RFI","No"].map(opt => (
                  <button
                    key={opt}
                    type="button"
                    data-testid={`dm-${dm}-verified-${opt.toLowerCase()}`}
                    className={`px-2 py-1 text-[11px] rounded-md border tracking-wider font-semibold ${
                      verified === opt
                        ? (opt === "Yes" ? "bg-emerald-600 text-white border-emerald-600"
                           : opt === "RFI" ? "bg-amber-500 text-white border-amber-500"
                           : "bg-red-600 text-white border-red-600")
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                    }`}
                    onClick={() => setField(dm, "verified", opt)}
                  >
                    {opt.toUpperCase()}
                  </button>
                ))}
              </div>
              <input
                type="text"
                data-testid={`dm-${dm}-method`}
                placeholder="How was this verified? (free text → Form 2 H)"
                className="col-span-12 md:col-span-4 px-2 py-1 border border-slate-300 rounded-md text-[12px]"
                value={method}
                onChange={(e) => setField(dm, "method", e.target.value)}
              />
              <div className="col-span-12 md:col-span-1 flex md:justify-end">
                <button
                  type="button"
                  disabled={autofilling[dm]}
                  onClick={() => autoFill(dm)}
                  data-testid={`dm-${dm}-autofill`}
                  title="Run Photo Checker + CET Verifier on this DM and draft the method text"
                  className={`px-2 py-1 text-[11px] tracking-wider font-semibold rounded-md border ${
                    autofilling[dm]
                      ? "bg-slate-200 text-slate-400 border-slate-300 cursor-not-allowed"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"
                  }`}
                >
                  {autofilling[dm] ? "…" : "Auto"}
                </button>
              </div>
              <div className="col-span-12 md:col-span-2 flex md:justify-end">
                <button
                  type="button"
                  disabled={!dirty || saving[dm]}
                  onClick={() => save(dm)}
                  data-testid={`dm-${dm}-save`}
                  className={`px-3 py-1 text-[11px] tracking-wider font-semibold rounded-md ${
                    dirty && !saving[dm]
                      ? "bg-slate-900 text-white hover:bg-slate-700"
                      : "bg-slate-200 text-slate-400 cursor-not-allowed"
                  }`}
                >
                  {saving[dm] ? "Saving…" : (dirty ? "Save" : "Saved")}
                </button>
              </div>
              <DMEvidenceDrawer jobId={job.id} dm={dm} expanded={isOpen} />
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 mt-2">Click a DM number to inspect its photos and verifier flags · click the dollar value to view the full CET workings (submitted vs verified, P50 vs P90, cost bands). Decisions persist on the job and flow into Form 2 columns G + H the next time you download Form 1+2 .xlsx or the Verified Package .zip.</p>

      {/* Shared CET workings modal — fires when the assessor clicks a DM row's dollar value */}
      {cetModalDM && (
        <CETSlipBreakdown
          jobId={job.id}
          dm={cetModalDM}
          hasCet
          hasSlip
          hideTrigger
          externalOpen
          onExternalClose={() => setCetModalDM(null)}
        />
      )}
    </div>
  );
}


/* ---------- EW Finalisation — inline ledger + per-defect decisions ---------- */
export function EWLedgerPanel({ jobId, initial, initialOverrides, onChange }) {
  const [file, setFile] = useState(null);
  const [fy, setFy] = useState(initial?.fy || "FY24-25");
  const [entityType, setEntityType] = useState(initial?.entity_type || "council");
  // `uploaded` holds an in-progress upload result that supersedes the parent's saved value
  // until the parent reloads (which will pass it as `initial`).
  const [uploaded, setUploaded] = useState(null);
  const data = uploaded || initial || null;
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(!initial);
  const [overrides, setOverrides] = useState(initialOverrides || {});
  const [selected, setSelected] = useState({});
  const [savingOverrides, setSavingOverrides] = useState(false);
  const saveTimer = useRef(null);
  const { user: ledgerUser } = useAuth();

  // Initialise overrides from parent prop ONCE on mount. The component manages its
  // own state thereafter and never re-syncs (avoids overriding in-flight edits).
  const initOverridesRef = useRef(initialOverrides);
  if (initOverridesRef.current === undefined) initOverridesRef.current = initialOverrides;

  // Debounced save: every override change schedules a save 600ms later
  const scheduleSave = useCallback((nextOverrides) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSavingOverrides(true);
      try {
        await axios.post(`${API}/ingest/jobs/${jobId}/ledger-check/overrides`, {
          overrides: nextOverrides,
          assessor_email: ledgerUser?.email || "",
          assessor_name: ledgerUser?.name || "",
        });
        if (onChange) onChange();
      } finally {
        setSavingOverrides(false);
      }
    }, 600);
  }, [jobId, onChange]);

  const setRowOverride = (rowId, patch) => {
    setOverrides(prev => {
      const cur = { ...(prev[rowId] || {}) };
      Object.assign(cur, patch);
      let next;
      if (cur.verdict == null && cur.oncost_applied == null && !cur.reason) {
        next = { ...prev }; delete next[rowId];
      } else {
        next = { ...prev, [rowId]: cur };
      }
      scheduleSave(next);
      return next;
    });
  };
  const clearOverrides = () => { setOverrides({}); setSelected({}); scheduleSave({}); };
  const bulkSet = (patch) => {
    const ids = Object.keys(selected).filter(id => selected[id]);
    if (ids.length === 0) return;
    setOverrides(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = { ...(next[id] || {}), ...patch }; });
      scheduleSave(next);
      return next;
    });
  };

  const upload = async (f) => {
    if (!f) return;
    setFile(f); setErr(null); setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("fy", fy);
      fd.append("entity_type", entityType);
      const r = await axios.post(`${API}/ingest/jobs/${jobId}/ledger-check`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(r.data);
      setOverrides({});
      if (onChange) onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to parse ledger.");
    } finally {
      setLoading(false);
    }
  };

  const clear = async () => {
    await axios.delete(`${API}/ingest/jobs/${jobId}/ledger-check`);
    setUploaded(null); setFile(null); setOverrides({});
    if (onChange) onChange();
  };

  // Detect ledger in package on mount so we can offer one-click auto-load
  const [detected, setDetected] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/ingest/jobs/${jobId}/ledger-check/detect`)
      .then(r => { if (!cancelled) setDetected(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [jobId]);

  const autoLoad = async () => {
    setAutoLoading(true); setErr(null);
    try {
      const r = await axios.post(`${API}/ingest/jobs/${jobId}/ledger-check/auto?fy=${fy}&entity_type=${entityType}`);
      setUploaded(r.data);
      setOverrides({});
      if (onChange) onChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Auto-load failed.");
    } finally {
      setAutoLoading(false);
    }
  };

  // Re-apply overrides client-side for instant feedback (backend will catch up in 600ms)
  const overridden = useMemo(() => applyLedgerOverrides(data, overrides), [data, overrides]);
  const totals = overridden?.totals || {};
  const counts = overridden?.counts || {};
  const rows = (overridden?.rows || []).filter(r => filter === "all" || r.eligibility === filter);
  const overrideCount = Object.keys(overrides).length;
  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="card p-5" data-testid="ew-ledger-panel">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between gap-3"
        data-testid="ew-ledger-toggle"
      >
        <div className="flex items-center gap-2">
          <ScrollText className="w-4 h-4 text-emerald-700"/>
          <h3 className="font-heading text-lg font-semibold">Ledger reconciliation</h3>
          {data && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] tracking-[0.14em] uppercase font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"/>
              Assessed · {fmtAUD(totals.assessed)}
            </span>
          )}
        </div>
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`}/>
      </button>
      {!expanded && (
        <p className="text-[12px] text-slate-500 mt-1">
          {data
            ? `${data.uploaded_filename || "Ledger"} · ${counts.total || 0} lines · Eligible ${fmtAUD(totals.eligible)} · Stripped ${fmtAUD(totals.stripped)} · On-costs reapplied ${fmtAUD(totals.oncosts)}`
            : "Upload the council's GL/MYOB export. We strip submitted on-cost lines and re-apply at the published Fact Sheet rate against eligible labour only."
          }
        </p>
      )}

      {expanded && (
        <div className="mt-4 space-y-3">
          {/* Auto-load CTA — surfaces when we detected a ledger in the package and no result yet */}
          {!data && detected && detected.candidates && detected.candidates.length > 0 && detected.candidates[0].available && (
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-emerald-300 bg-emerald-50" data-testid="ew-ledger-autoload-cta">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.16em] uppercase font-bold text-emerald-800 mb-1">Ledger detected in package</div>
                <div className="font-mono text-[12px] text-slate-800 truncate" title={detected.candidates[0].rel_path}>{detected.candidates[0].name}</div>
                <div className="text-[10.5px] text-slate-600 mt-0.5">{fmtBytes(detected.candidates[0].size)} · classified as {detected.candidates[0].source === "financial_summary" ? "Financial Summary" : "Ledger (name match)"}. One-click parse + classify against DRFA rules.</div>
              </div>
              <button
                onClick={autoLoad}
                disabled={autoLoading}
                data-testid="ew-ledger-autoload-btn"
                className={`whitespace-nowrap text-[11px] tracking-wider uppercase font-bold px-4 py-2 rounded-md ${autoLoading ? "bg-slate-300 text-slate-500 cursor-not-allowed" : "bg-emerald-600 text-white hover:bg-emerald-700"}`}
              >
                {autoLoading ? "Loading…" : "Auto-load"}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <label className="md:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Ledger file (.csv / .xlsx)</div>
              <input
                type="file"
                accept=".csv,.xlsx,.xlsm,.xls"
                data-testid="ew-ledger-file-input"
                onChange={(e) => upload(e.target.files?.[0])}
                className="block w-full text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer hover:file:bg-slate-700"
              />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Financial Year</div>
              <select
                value={fy}
                onChange={(e) => setFy(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="ew-ledger-fy-select"
              >
                <option value="FY22-23">FY22-23 · 32.677%</option>
                <option value="FY23-24">FY23-24 · 32.891%</option>
                <option value="FY24-25">FY24-25 · 33.463%</option>
                <option value="FY25-26">FY25-26 · 34.232%</option>
              </select>
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500 mb-2">Entity type</div>
              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="ew-ledger-entity-select"
              >
                <option value="council">Council (opt-in)</option>
                <option value="state_agency">State Agency</option>
              </select>
            </label>
          </div>
          {file && <div className="text-[11.5px] font-mono text-slate-500">Selected: {file.name} · {(file.size/1024).toFixed(1)} KB</div>}
          {loading && <div className="flex items-center gap-2 text-sm text-slate-600"><Loader2 className="w-4 h-4 animate-spin"/> Parsing ledger…</div>}
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}

          {data && (
            <div className="space-y-3" data-testid="ew-ledger-results">
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-emerald-700">Eligible</div><div className="font-mono font-semibold text-emerald-800">{fmtAUD(totals.eligible)} <span className="text-[10px] text-emerald-600">({counts.eligible})</span></div></div>
                <div className="bg-red-50 border border-red-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-red-700">Ineligible</div><div className="font-mono font-semibold text-red-800">{fmtAUD(totals.ineligible)} <span className="text-[10px] text-red-600">({counts.ineligible})</span></div></div>
                <div className="bg-amber-50 border border-amber-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-amber-700">Review</div><div className="font-mono font-semibold text-amber-800">{fmtAUD(totals.review)} <span className="text-[10px] text-amber-600">({counts.review})</span></div></div>
                <div className="bg-blue-50 border border-blue-200 rounded p-2" title="Submitted oncost lines — stripped from totals. Reapplied by assessor on eligible labour only."><div className="text-[9px] uppercase tracking-wider text-blue-700">Stripped (submitted)</div><div className="font-mono font-semibold text-blue-800">{fmtAUD(totals.stripped)} <span className="text-[10px] text-blue-600">({counts.stripped})</span></div></div>
                <div className="bg-slate-50 border border-slate-200 rounded p-2"><div className="text-[9px] uppercase tracking-wider text-slate-500">On-costs reapplied<br/><span className="font-mono text-[9.5px]">{data.oncost_pct_ordinary}% · OT {data.oncost_pct_overtime}%</span></div><div className="font-mono font-semibold text-slate-800">{fmtAUD(totals.oncosts)}</div></div>
                <div className="bg-slate-900 border border-slate-900 rounded p-2 text-white"><div className="text-[9px] uppercase tracking-wider text-white/70">Assessed total</div><div className="font-mono font-semibold">{fmtAUD(totals.assessed)}</div></div>
              </div>

              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-700">
                  Assessed ledger · {rows.length} line{rows.length === 1 ? "" : "s"} {data.uploaded_filename && <span className="text-slate-400 font-normal">· {data.uploaded_filename}</span>}
                  {overrideCount > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-300">
                      {overrideCount} assessor override{overrideCount === 1 ? "" : "s"}
                      {savingOverrides && <span className="text-amber-600 ml-1">· saving…</span>}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-[11px]" data-testid="ew-ledger-filter">
                    <option value="all">All</option><option value="eligible">Eligible only</option>
                    <option value="ineligible">Ineligible only</option><option value="review">Review only</option>
                    <option value="stripped">Stripped (submitted)</option>
                  </select>
                  {overrideCount > 0 && (
                    <button onClick={clearOverrides} className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50" data-testid="ew-ledger-clear-overrides">Clear overrides</button>
                  )}
                  <a
                    href={`${BACKEND_URL}/api/ingest/jobs/${jobId}/ledger-check/download`}
                    download
                    className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 bg-slate-900 text-white rounded hover:bg-slate-700"
                    data-testid="ew-ledger-download"
                  >Download assessed .xlsx</a>
                  <button onClick={clear} className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50" data-testid="ew-ledger-clear">Clear ledger</button>
                </div>
              </div>

              {selectedCount > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 border border-slate-300 rounded text-[11px] flex-wrap" data-testid="ew-ledger-bulk-bar">
                  <span className="font-bold">{selectedCount} selected</span>
                  <span className="text-slate-400">·</span>
                  <button onClick={() => bulkSet({ verdict: "ineligible" })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700">Mark Ineligible</button>
                  <button onClick={() => bulkSet({ verdict: "eligible" })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700">Mark Eligible</button>
                  <button onClick={() => bulkSet({ verdict: "review" })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-amber-500 text-white rounded hover:bg-amber-600">Mark Review</button>
                  <button onClick={() => bulkSet({ oncost_applied: false })} className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 bg-slate-700 text-white rounded hover:bg-slate-800">Strip on-costs</button>
                  <button onClick={() => setSelected({})} className="ml-auto text-[10px] uppercase tracking-wider font-semibold px-2 py-1 border border-slate-300 rounded hover:bg-white">Clear selection</button>
                </div>
              )}

              <div className="overflow-x-auto border border-slate-200 rounded max-h-[420px] overflow-y-auto">
                <table className="w-full text-[10.5px]">
                  <thead className="bg-slate-100 sticky top-0"><tr className="text-left text-slate-600">
                    <th className="p-1.5 w-7"><input type="checkbox" data-testid="ew-ledger-select-all"
                      onChange={(e) => {
                        const all = e.target.checked;
                        const nxt = {};
                        if (all) rows.slice(0, 200).forEach(r => { if (r.eligibility !== "stripped") nxt[r.row_id] = true; });
                        setSelected(nxt);
                      }}/></th>
                    <th className="p-1.5">Date</th><th className="p-1.5">Account</th><th className="p-1.5">Description</th>
                    <th className="p-1.5 text-right">Amount</th><th className="p-1.5">Eligibility</th>
                    <th className="p-1.5 min-w-[220px]">Assessor verdict + on-cost</th>
                    <th className="p-1.5 text-right">On-cost</th><th className="p-1.5 text-right">Assessed</th>
                  </tr></thead>
                  <tbody>
                    {rows.slice(0, 200).map((r) => {
                      const tone = r.eligibility === "eligible" ? "bg-emerald-50"
                        : r.eligibility === "ineligible" ? "bg-red-50"
                        : r.eligibility === "stripped" ? "bg-blue-50 line-through opacity-75"
                        : "bg-amber-50";
                      const overriddenBorder = r.was_overridden ? "border-l-2 border-l-amber-500" : "";
                      return (
                        <tr key={r.row_id} className={`border-t border-slate-100 ${tone} ${overriddenBorder}`}>
                          <td className="p-1.5">
                            {r.eligibility !== "stripped" && (
                              <input type="checkbox"
                                checked={!!selected[r.row_id]}
                                onChange={(e) => setSelected(s => ({ ...s, [r.row_id]: e.target.checked }))}/>
                            )}
                          </td>
                          <td className="p-1.5 whitespace-nowrap font-mono">{r.date || "—"}</td>
                          <td className="p-1.5 truncate max-w-[120px]" title={r.account}>{r.account || "—"}</td>
                          <td className="p-1.5 truncate max-w-[240px]" title={r.description}>{r.description || "—"}</td>
                          <td className="p-1.5 font-mono text-right">{fmtAUD(r.amount)}</td>
                          <td className="p-1.5 capitalize font-semibold">
                            {r.eligibility}
                            {r.was_overridden && <span className="ml-1 text-amber-600" title={`Overridden — tool said ${r.tool_eligibility}`}>⚙</span>}
                          </td>
                          <td className="p-1.5">
                            <LedgerRowOverrideControls row={r} onChange={(patch) => setRowOverride(r.row_id, patch)} dense/>
                          </td>
                          <td className="p-1.5 font-mono text-right">{r.oncost_aud ? fmtAUD(r.oncost_aud) : "—"}</td>
                          <td className="p-1.5 font-mono text-right">{fmtAUD(r.assessed_aud)}</td>
                        </tr>
                      );
                    })}
                    {rows.length > 200 && (
                      <tr><td colSpan={9} className="p-2 text-[11px] text-slate-500 italic text-center">… first 200 of {rows.length} rows shown. Use filter to narrow.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function EWPhotoStrip({ label, items, color }) {
  if (!items || items.length === 0) {
    return <div className="text-[11px] text-slate-400 italic">No {label.toLowerCase()} photos uploaded.</div>;
  }
  return (
    <div>
      <div className={`text-[10px] tracking-[0.14em] uppercase font-bold mb-1.5 ${color}`}>{label} · {items.length}</div>
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {items.slice(0, 12).map((p) => (
          <a key={`${p.bucket}-${p.idx}`} href={`${BACKEND_URL}${p.url}`} target="_blank" rel="noopener noreferrer" className="shrink-0 group" title={p.name}>
            <img src={`${BACKEND_URL}${p.url}`} alt={p.name} className="h-20 w-28 object-cover rounded border border-slate-200 group-hover:border-slate-500"/>
          </a>
        ))}
        {items.length > 12 && <div className="shrink-0 h-20 w-16 rounded border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-500">+{items.length - 12}</div>}
      </div>
    </div>
  );
}

export function EWDefectPhotos({ jobId, defectId, photoCounts }) {
  const [photos, setPhotos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) { setLoading(true); setErr(null); setPhotos(null); }
    });
    axios.get(`${API}/ingest/jobs/${jobId}/ew-photos/${defectId}`)
      .then(r => { if (!cancelled) setPhotos(r.data); })
      .catch(e => { if (!cancelled) setErr(e?.response?.data?.detail || e?.message || "Failed to load photos"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [jobId, defectId]);

  if (loading) return <div className="text-[11px] text-slate-500 italic">Loading photos…</div>;
  if (err) return <div className="text-[11px] text-red-600">{err}</div>;
  if (!photos) return null;
  if (!photos.available) return <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">Original ingest folder has been cleaned up — photo previews unavailable. Counts: {photoCounts?.damage || 0} post-event, {photoCounts?.completion || 0} completion.</div>;

  return (
    <div className="space-y-3">
      <EWPhotoStrip label="Post-event (damage) evidence" items={photos.damage} color="text-red-700"/>
      <EWPhotoStrip label="Completion photos" items={photos.completion} color="text-emerald-700"/>
      {photos.pre && photos.pre.length > 0 && (
        <EWPhotoStrip label="Pre-condition evidence" items={photos.pre} color="text-slate-700"/>
      )}
    </div>
  );
}

export function EWDefectRow({ jobId, defect, decision, photoCounts, onSave }) {
  const { user: defectUser } = useAuth();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const cur = { ...decision, ...draft };
  const verified = cur.verified || "";
  const dirty = Object.keys(draft).length > 0;

  const setField = (k, v) => setDraft(d => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        defect_id: defect.defect_id,
        ...draft,
        assessor_email: defectUser?.email || "",
        assessor_name: defectUser?.name || "",
      };
      await axios.post(`${API}/ingest/jobs/${jobId}/defect-decision`, payload);
      setDraft({});
      if (onSave) onSave();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white" data-testid={`defect-row-${defect.defect_id}`}>
      <div className="grid grid-cols-12 gap-3 p-3 items-start">
        <div className="col-span-12 md:col-span-4">
          <button
            type="button"
            onClick={() => setExpanded(e => !e)}
            className="flex items-start gap-1.5 text-left w-full hover:text-slate-900"
            data-testid={`defect-${defect.defect_id}-toggle`}
          >
            <ChevronRight className={`w-3.5 h-3.5 mt-1 transition-transform shrink-0 ${expanded ? "rotate-90" : ""}`}/>
            <div className="min-w-0">
              <div className="font-mono text-[11px] text-slate-500">{defect.defect_id}</div>
              <div className="font-semibold text-[13px] leading-tight truncate" title={defect.asset_name}>{defect.asset_name || "(unnamed asset)"}</div>
              {defect.scope && <div className="text-[11px] text-slate-600 mt-0.5 line-clamp-2">{defect.scope}</div>}
              <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500 font-mono">
                <span>{defect.asset_class}</span>
                <span>· post-event {photoCounts?.damage ?? 0}</span>
                <span>· completion {photoCounts?.completion ?? 0}</span>
              </div>
            </div>
          </button>
        </div>
        <div className="col-span-6 md:col-span-2 text-right">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500">Claimed (ex GST)</div>
          <div className="font-mono font-semibold text-[13px]">{fmtAUD(defect.ex_gst)}</div>
          {defect.completion_date && <div className="text-[10px] text-slate-500 font-mono mt-0.5">Compl. {defect.completion_date}</div>}
        </div>
        <div className="col-span-6 md:col-span-3">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-1">Council certification (D / C / S)</div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { k: "certify_damage", label: "D", title: "Damage certified" },
              { k: "certify_costs", label: "C", title: "Costs certified" },
              { k: "certify_substantial", label: "S", title: "Substantial completion certified" },
            ].map(({ k, label, title }) => {
              const on = !!cur[k];
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setField(k, !on)}
                  title={title}
                  data-testid={`defect-${defect.defect_id}-${k}`}
                  className={`px-2.5 py-1 text-[11px] rounded-md border font-bold tracking-wider ${
                    on
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                  }`}
                >
                  {label}{on ? " ✓" : ""}
                </button>
              );
            })}
          </div>
        </div>
        <div className="col-span-12 md:col-span-3">
          <div className="text-[10px] tracking-[0.14em] uppercase font-bold text-slate-500 mb-1">Verdict</div>
          <div className="flex gap-1">
            {["Yes", "RFI", "No"].map(opt => (
              <button
                key={opt}
                type="button"
                data-testid={`defect-${defect.defect_id}-verified-${opt.toLowerCase()}`}
                onClick={() => setField("verified", opt)}
                className={`flex-1 px-2 py-1 text-[11px] rounded-md border tracking-wider font-semibold ${
                  verified === opt
                    ? (opt === "Yes" ? "bg-emerald-600 text-white border-emerald-600"
                       : opt === "RFI" ? "bg-amber-500 text-white border-amber-500"
                       : "bg-red-600 text-white border-red-600")
                    : "bg-white text-slate-600 border-slate-300 hover:bg-slate-100"
                }`}
              >
                {opt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-12 flex gap-2 items-start">
          <input
            type="text"
            data-testid={`defect-${defect.defect_id}-comments`}
            placeholder="Assessor comments (flows into Spreadsheet column AB)"
            className="flex-1 px-2 py-1.5 border border-slate-300 rounded-md text-[12px]"
            value={cur.comments || ""}
            onChange={(e) => setField("comments", e.target.value)}
          />
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={save}
            data-testid={`defect-${defect.defect_id}-save`}
            className={`px-3 py-1.5 text-[11px] tracking-wider font-semibold rounded-md whitespace-nowrap ${
              dirty && !saving
                ? "bg-slate-900 text-white hover:bg-slate-700"
                : "bg-slate-200 text-slate-400 cursor-not-allowed"
            }`}
          >
            {saving ? "Saving…" : (dirty ? "Save" : "Saved")}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-slate-200 p-3 bg-slate-50">
          <EWDefectPhotos jobId={jobId} defectId={defect.defect_id} photoCounts={photoCounts}/>
        </div>
      )}
    </div>
  );
}

export function EWFinalisationPanel({ job }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showAllPhotos, setShowAllPhotos] = useState(false);
  const [councilStatus, setCouncilStatus] = useState("");

  const reload = useCallback(async () => {
    Promise.resolve().then(() => setLoading(true));
    try {
      const r = await axios.get(`${API}/ingest/jobs/${job.id}/ew-defects`);
      setData(r.data);
      setCouncilStatus(prev => prev || r.data?.council_status || "");
    } finally {
      setLoading(false);
    }
  }, [job.id]);

  useEffect(() => { reload(); }, [reload]);

  const progress = useMemo(() => {
    if (!data) return null;
    const defects = data.defects || [];
    const decs = data.decisions || {};
    let yes = 0, rfi = 0, no = 0, undecided = 0;
    let dCount = 0, cCount = 0, sCount = 0;
    for (const d of defects) {
      const v = decs[d.defect_id] || {};
      if (v.verified === "Yes") yes++;
      else if (v.verified === "RFI") rfi++;
      else if (v.verified === "No") no++;
      else undecided++;
      if (v.certify_damage) dCount++;
      if (v.certify_costs) cCount++;
      if (v.certify_substantial) sCount++;
    }
    const total = defects.length;
    const decided = yes + rfi + no;
    const complete = total > 0 && undecided === 0;
    const ready = complete && rfi === 0 && no === 0;
    return { total, yes, rfi, no, decided, undecided, complete, ready, dCount, cCount, sCount };
  }, [data]);

  if (loading && !data) return <div className="card p-5 text-sm text-slate-500">Loading EW defects…</div>;
  if (!data) return null;

  const defects = (data.defects || []).filter(d => {
    if (filter === "all") return true;
    const v = (data.decisions || {})[d.defect_id]?.verified;
    if (filter === "undecided") return !v;
    return v === filter;
  });

  const persistCouncilStatus = async (val) => {
    setCouncilStatus(val);
    if (!data.defects || data.defects.length === 0) return;
    // Bubble up by writing on the first defect (server merges + sets job-level council_status)
    await axios.post(`${API}/ingest/jobs/${job.id}/defect-decision`, {
      defect_id: data.defects[0].defect_id,
      council_status: val,
    });
    reload();
  };

  return (
    <div className="space-y-5" data-testid="ew-finalisation-panel">
      {/* Header + readiness chip + download */}
      <div className="card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-heading text-lg font-semibold">EW Assessment Spreadsheet</h3>
              {progress && progress.total > 0 && (
                <span
                  data-testid="ew-readiness-chip"
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] tracking-[0.14em] uppercase font-bold ${
                    progress.ready
                      ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                      : progress.complete
                        ? "bg-amber-100 text-amber-800 border border-amber-300"
                        : "bg-slate-200 text-slate-700 border border-slate-300"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    progress.ready ? "bg-emerald-500"
                      : progress.complete ? "bg-amber-500"
                      : "bg-slate-500"
                  }`}/>
                  {progress.ready
                    ? "Ready to ship"
                    : progress.complete
                      ? `Decided · ${progress.rfi} RFI`
                      : `${progress.decided} of ${progress.total} defects decided`}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-600 max-w-2xl">
              {data.item_count} defect{data.item_count === 1 ? "" : "s"} parsed from {data.form_source || "the claim form"} · Total claimed {fmtAUD(data.total_claimed)}.
              {data.ledger_check && <> · Assessed ledger {fmtAUD(data.ledger_check.totals?.assessed)}.</>}
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end shrink-0">
            <label className="text-[11px]">
              <span className="text-[10px] tracking-[0.16em] uppercase font-bold text-slate-500 mr-2">Council status</span>
              <select
                value={councilStatus}
                onChange={(e) => persistCouncilStatus(e.target.value)}
                className="px-2 py-1 border border-slate-300 rounded text-[11px]"
                data-testid="ew-council-status"
              >
                <option value="">—</option>
                <option value="Opt-in">Opt-in</option>
                <option value="Opt-out">Opt-out</option>
              </select>
            </label>
            <a
              href={`${API}/ingest/jobs/${job.id}/ew-assessment-form`}
              className={`btn whitespace-nowrap ${progress?.ready ? "btn-primary ring-2 ring-emerald-400" : "btn-primary"}`}
              data-testid="download-ew-form-btn"
              download
            >
              Download EW Assessment .xlsx
            </a>
          </div>
        </div>
        {progress && progress.total > 0 && (
          <div className="mt-4 flex items-center gap-3 text-[11px] tracking-[0.14em] uppercase font-semibold" data-testid="ew-progress-bar">
            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden flex">
              <div className="bg-emerald-500 h-full" style={{ width: `${(progress.yes / progress.total) * 100}%` }}/>
              <div className="bg-amber-500 h-full" style={{ width: `${(progress.rfi / progress.total) * 100}%` }}/>
              <div className="bg-red-500 h-full" style={{ width: `${(progress.no / progress.total) * 100}%` }}/>
            </div>
            <div className="flex items-center gap-2 text-slate-600 text-[10.5px]">
              <span className="text-emerald-700">{progress.yes} YES</span>
              <span className="text-amber-700">{progress.rfi} RFI</span>
              <span className="text-red-700">{progress.no} NO</span>
              {progress.undecided > 0 && <span className="text-slate-500">{progress.undecided} pending</span>}
              <span className="text-slate-400">·</span>
              <span className="text-slate-600">D {progress.dCount} · C {progress.cCount} · S {progress.sCount}</span>
            </div>
          </div>
        )}
      </div>

      {/* Ledger Checker */}
      <EWLedgerPanel jobId={job.id} initial={data.ledger_check} initialOverrides={data.ledger_check_overrides} onChange={reload}/>

      {/* Per-defect rows */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-heading text-lg font-semibold">Per-defect decisions</h3>
            <p className="text-[12px] text-slate-500">Yes / RFI / No verdict + council D/C/S certification. Decisions flow into the spreadsheet outcome + recommendation columns.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-[11px]" data-testid="ew-defect-filter">
              <option value="all">All defects</option>
              <option value="undecided">Undecided</option>
              <option value="Yes">Yes only</option>
              <option value="RFI">RFI only</option>
              <option value="No">No only</option>
            </select>
            <button
              type="button"
              onClick={() => setShowAllPhotos(s => !s)}
              className="text-[10px] uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50"
              data-testid="ew-toggle-photos"
            >
              {showAllPhotos ? "Hide photos" : "Show photos"}
            </button>
          </div>
        </div>
        {defects.length === 0 ? (
          (data.defects || []).length === 0 && (data.item_count || 0) > 0 ? (
            // Header parse populated but the per-defect list is empty —
            // this happens when the claim_items array was wiped between
            // ingestion and now (e.g. an interrupted re-parse). Surface
            // the issue clearly with a one-click re-parse so the assessor
            // isn't stuck.
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900 flex items-start gap-2.5" data-testid="ew-defects-empty-warning">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600"/>
              <div className="flex-1">
                <div className="font-semibold">{data.item_count} defects parsed from the header but the per-defect list is empty.</div>
                <div className="text-[11.5px] mt-0.5 text-amber-800/90">
                  Most likely cause: the claim-form parse was interrupted, or the items array was cleared by an earlier re-ingest. Click <b>Re-parse claim form</b> above to rebuild the per-defect rows.
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500 italic">No defects match this filter.</div>
          )
        ) : (
          <div className="space-y-2">
            {defects.map(d => (
              <div key={d.defect_id}>
                <EWDefectRow
                  jobId={job.id}
                  defect={d}
                  decision={(data.decisions || {})[d.defect_id] || {}}
                  photoCounts={(data.photos_by_defect || {})[d.defect_id] || {}}
                  onSave={reload}
                />
                {showAllPhotos && (
                  <div className="mt-1 border border-slate-200 rounded p-3 bg-slate-50">
                    <EWDefectPhotos jobId={job.id} defectId={d.defect_id} photoCounts={(data.photos_by_defect || {})[d.defect_id] || {}}/>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


export function IngestJob() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(
    () => axios.get(`${API}/ingest/jobs/${jobId}`).then(r => setJob(r.data)).catch(() => setJob({ error: "Not found" })),
    [jobId],
  );
  useEffect(() => { load(); }, [load]);

  // Live-poll the job document while it's still being ingested so the
  // progress bar at the top of the page ticks forward in real time. Stops
  // automatically once ingest_status flips out of 'processing'.
  useEffect(() => {
    if (!job || job.error) return undefined;
    if (job.ingest_status !== "processing") return undefined;
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [job?.ingest_status, job?.error, load]);

  // Auto-claim the job for the signed-in assessor on first open. Starts the
  // SLA clock and surfaces it on the assessor's Dashboard "Resume" panel.
  const claimedRef = useRef(new Set());
  useEffect(() => {
    if (!job || !job.id || job.error) return;
    if (!user?.email) return;
    const alreadyMine = job.assigned_to === user.email;
    if (alreadyMine && job.assessor_started_at) return;
    if (claimedRef.current.has(job.id)) return;
    claimedRef.current.add(job.id);
    axios.post(`${API}/ingest/jobs/${job.id}/claim`, {
      assessor_email: user.email,
      assessor_name: user.name || "",
    }).then(r => setJob(r.data)).catch(() => {});
  }, [job, user?.email, user?.name]);

  // Self-heal: when a job opens with 0 photo evidence but the package contains
  // evidence archives (nested ZIPs from SmartyGrants), auto-run the re-walk
  // endpoint once so the assessor sees the correct counts on the first view.
  const selfHealedRef = useRef(new Set());
  useEffect(() => {
    if (!job || !job.id || job.error) return;
    if (selfHealedRef.current.has(job.id)) return;
    const ev = job.evidence || {};
    const fileKinds = job.file_kinds || {};
    const totalPhotos = (ev.total_damage_photos || 0) + (ev.total_completion_photos || 0) + (ev.total_pre_photos || 0);
    const hasEvidenceArchives = (fileKinds.evidence_archive || 0) > 0 || (fileKinds.image || 0) > 0;
    if (totalPhotos === 0 && hasEvidenceArchives) {
      selfHealedRef.current.add(job.id);
      axios.post(`${API}/ingest/jobs/${job.id}/rewalk-evidence`).then(() => load()).catch(() => {});
    }
  }, [job, load]);

  const resolve = async (idx, resolution, value=null) => {
    setBusy(true);
    try { await axios.post(`${API}/ingest/jobs/${jobId}/resolve`, { item_index: idx, resolution, value }); load(); }
    finally { setBusy(false); }
  };

  // Per-DM verification progress — drives the sticky readiness chip + Form 2 summary.
  // Must be declared before any early returns so hooks fire in a stable order.
  const isEPAR = (job?.portal_type || "").startsWith("EPAR");
  // EW Finalisation + IRW Finalisation share the same single-claim defect-based
  // UX (no DMs, no CET workings — just photos + ledger + per-defect decisions).
  const isEW = (job?.portal_type || "").startsWith("EW") || job?.portal_type === "IRW Finalisation";
  const progress = useMemo(() => {
    if (!job || job.error || !isEPAR) return null;
    const rows = (job?.slip_estimates_rollup?.rows && job.slip_estimates_rollup.rows.length > 0)
      ? job.slip_estimates_rollup.rows.map(r => r.dm_no || r.dm_no_summary)
      : (job?.cet_rollup?.rows || []).map(r => r.dm);
    const total = rows.filter(Boolean).length;
    const decisions = job?.dm_decisions || {};
    const lookup = {};
    for (const [k, v] of Object.entries(decisions)) {
      lookup[(k || "").toUpperCase()] = v;
      for (const piece of (k || "").split(/[\/,;]+/)) {
        const p = piece.trim().toUpperCase();
        if (p) lookup[p] = lookup[p] || v;
      }
    }
    let yes = 0, rfi = 0, no = 0, undecided = 0;
    for (const dm of rows) {
      const key = (dm || "").toUpperCase();
      let d = lookup[key];
      if (!d) {
        for (const piece of (dm || "").split(/[\/,;]+/)) {
          const p = piece.trim().toUpperCase();
          if (lookup[p]) { d = lookup[p]; break; }
        }
      }
      const v = d?.verified;
      if (v === "Yes") yes++;
      else if (v === "RFI") rfi++;
      else if (v === "No") no++;
      else undecided++;
    }
    const decided = yes + rfi + no;
    const complete = total > 0 && undecided === 0;
    const ready = complete && rfi === 0 && no === 0;
    return { total, yes, rfi, no, decided, undecided, complete, ready };
  }, [isEPAR, job]);

  if (!job) return <div className="container py-8 text-slate-500">Loading…</div>;
  if (job.error) return <div className="container py-8 text-red-600">{job.error}</div>;

  const slipCount = job?.slip_estimates_rollup?.count ?? 0;
  const cetCount = job?.cet_rollup?.dm_count ?? 0;
  const totalErc = job?.cet_rollup?.total_erc ?? job?.slip_estimates_rollup?.total_pricing ?? 0;
  return (
    <div className="container py-8 max-w-6xl" data-testid="inbox-job-page">
      <Link to="/inbox" className="text-xs text-blue-600 hover:underline">← Back to inbox</Link>
      <div className="label-upper mt-3 mb-1">Ingest package</div>
      <h1 className="font-heading text-3xl font-bold mb-1">{job.top_dir_display || job.top_dir || job.id}</h1>
      <div className="text-sm text-slate-500 mb-3">{job.portal_type} · auto-detected at {(job.portal_confidence*100).toFixed(0)}% confidence</div>

      {job.ingest_status === "processing" && (
        <div className="card p-4 mb-4 border-blue-200 bg-blue-50/40" data-testid="ingestjob-processing-banner">
          <div className="text-[10px] tracking-[0.18em] uppercase font-bold text-blue-700 mb-1">Ingest in progress</div>
          <IngestProgressInline progress={job.ingest_progress} jobId={job.id} />
          <div className="text-[11px] text-slate-600 mt-2">
            Heavy CET workbooks + nested evidence ZIPs can take 1-3 minutes for large packages. This page will update automatically.
          </div>
        </div>
      )}

      {/* SLA + assignment chip — drives the assessor's Resume panel and audit trail. */}
      <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="ingestjob-sla-bar">
        <SLAPill sla={job.sla} testid="ingestjob-sla-pill" />
        {job.assigned_to && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] tracking-[0.1em] uppercase font-semibold" data-testid="ingestjob-assignee">
            <ShieldCheck className="w-3 h-3"/> {job.assigned_to_name || job.assigned_to}
          </span>
        )}
        {!job.assessor_completed_at && job.assessor_started_at && (
          <button
            type="button"
            data-testid="ingestjob-finalise-btn"
            onClick={async () => {
              if (!window.confirm("Mark this assessment as complete? This stops the SLA clock and moves the job off your Resume panel. You can reopen it later if needed.")) return;
              try {
                const r = await axios.post(`${API}/ingest/jobs/${job.id}/finalise`);
                setJob(r.data);
              } catch (e) {
                alert("Finalise failed: " + (e?.response?.data?.detail || e?.message));
              }
            }}
            className="px-2.5 py-1 text-[11px] tracking-[0.12em] uppercase font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
          >Mark assessment complete</button>
        )}
        {job.assessor_completed_at && (
          <button
            type="button"
            data-testid="ingestjob-reopen-btn"
            onClick={async () => {
              try {
                const r = await axios.post(`${API}/ingest/jobs/${job.id}/reopen`);
                setJob(r.data);
              } catch (e) {
                alert("Reopen failed: " + (e?.response?.data?.detail || e?.message));
              }
            }}
            className="px-2.5 py-1 text-[11px] tracking-[0.12em] uppercase font-semibold rounded-md bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50"
          >Reopen assessment</button>
        )}
      </div>

      {/* Claim value banner — shows the council's current effective claim with
          the lifecycle context (Form 1 → RFI revision → Evidentiary CET). The
          assessor needs to see at a glance how the value has evolved across
          the RFI rounds, NOT just the bottom-line number. */}
      <ClaimValueBanner job={job} />

      {/* Quick-jump shortcuts to the heavy tool panels — single click from anywhere on the page */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="ingestjob-shortcuts">
        {isEW && (
          <button
            type="button"
            data-testid="shortcut-ledger-checker"
            onClick={() => {
              const el = document.querySelector("[data-testid='ew-ledger-panel']");
              if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "start" });
                // Auto-expand the ledger panel
                const toggle = el.querySelector("[data-testid='ew-ledger-toggle']");
                if (toggle) {
                  const ariaExp = el.querySelector("[data-testid='ew-ledger-results']");
                  if (!ariaExp) toggle.click();
                }
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 text-[11px] tracking-wider uppercase font-bold hover:bg-emerald-100"
          >
            <ScrollText className="w-3.5 h-3.5"/> Ledger Checker
          </button>
        )}
        <button
          type="button"
          data-testid="shortcut-source-tree"
          onClick={() => {
            const el = document.querySelector("[data-testid='source-tree']");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 text-[11px] tracking-wider uppercase font-bold hover:bg-slate-50"
        >
          <FileText className="w-3.5 h-3.5"/> File tree
        </button>
        {isEPAR && (
          <button
            type="button"
            data-testid="shortcut-dm-decisions"
            onClick={() => {
              const el = document.querySelector("[data-testid='dm-decisions-block']");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 text-[11px] tracking-wider uppercase font-bold hover:bg-slate-50"
          >
            Per-DM verification
          </button>
        )}
        {/* RFI Response upload + diff approval (per-submission entry point). */}
        <RFIResponseButton job={job} onChange={load} />
        {/* Evidentiary CET — rebuilds the council's CET inside the official NSWRA v2.1 blank template. */}
        {isEPAR && <EvidentiaryCETButton job={job} />}
        <button
          type="button"
          data-testid="shortcut-rewalk-photos"
          onClick={async () => {
            try {
              const r = await axios.post(`${API}/ingest/jobs/${job.id}/rewalk-evidence`);
              const e = r.data.evidence || {};
              alert(`Re-scanned ${e.road_count} DM groups · ${e.total_damage_photos} damage · ${e.total_completion_photos} completion · ${e.total_pre_photos} pre-event photos. Reloading page…`);
              window.location.reload();
            } catch (err) {
              alert("Re-scan failed: " + (err?.response?.data?.detail || err.message));
            }
          }}
          title="Re-walk the ingest folder and refresh photo counts. Use after upgrading the backend classifier."
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 text-[11px] tracking-wider uppercase font-bold hover:bg-slate-50"
        >
          <ImageIcon className="w-3.5 h-3.5"/> Re-scan photos
        </button>
        <button
          type="button"
          data-testid="shortcut-reparse-claim"
          onClick={async () => {
            try {
              const r = await axios.post(`${API}/ingest/jobs/${job.id}/reparse-claim-form`);
              alert(`Re-parsed claim form · source: ${r.data.form_source} · ${r.data.item_count} defect(s) · total $${(r.data.total_aud || 0).toLocaleString()}. Reloading…`);
              window.location.reload();
            } catch (err) {
              alert("Re-parse failed: " + (err?.response?.data?.detail || err.message));
            }
          }}
          title="Re-run the claim form parser (Form 306 / SG Assessment Spreadsheet) against this package. Use to retroactively pick up parser improvements without a full re-ingest."
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-300 bg-white text-slate-700 text-[11px] tracking-wider uppercase font-bold hover:bg-slate-50"
        >
          Re-parse claim form
        </button>
        <button
          type="button"
          data-testid="shortcut-ai-extract-pdf"
          onClick={async () => {
            if (!window.confirm("Use Claude Sonnet 4.5 to extract defects from the SmartyGrants application PDF? This is for packages without a structured assessment spreadsheet — may take 10-30s.")) return;
            try {
              const r = await axios.post(`${API}/ingest/jobs/${job.id}/extract-defects-from-pdf`);
              const issues = r.data.issues || [];
              alert(`AI extracted ${r.data.item_count} defect(s) · total $${(r.data.total_aud || 0).toLocaleString()}` + (issues.length ? `\n\n${issues.length} parsing concern(s) added to Needs Review.` : "") + "\n\nReloading…");
              window.location.reload();
            } catch (err) {
              alert("AI extract failed: " + (err?.response?.data?.detail || err.message));
            }
          }}
          title="When the package has no structured assessment spreadsheet, ask Claude Sonnet 4.5 to extract defects from the SmartyGrants application PDF. The LLM presents the structured data — the assessor makes the final decision per defect."
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-violet-300 bg-violet-50 text-violet-800 text-[11px] tracking-wider uppercase font-bold hover:bg-violet-100"
        >
          AI-extract from PDF
        </button>
        <Link
          to="/security"
          data-testid="ai-readonly-badge"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 text-[10px] tracking-[0.14em] uppercase font-bold hover:bg-emerald-100"
          title="The AI extracts and surfaces options. The SQP — not the AI — clicks the assessment outcome. Read the full Security & AI statement."
        >
          <ShieldCheck className="w-3 h-3"/> AI: read-only · SQP decides
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        {isEPAR ? (
          <>
            <Stat label="Damage Memos (CETs)" value={cetCount || "—"} mono />
            <Stat label="Form 2 line items (slip est.)" value={slipCount || "—"} mono />
            <Stat label="Total ERC" value={fmtAUD(totalErc)} mono />
            <Stat label="Damage photos" value={job?.evidence?.total_damage_photos ?? "—"} mono />
          </>
        ) : (
          <>
            <Stat label="Line items" value={job?.form_306?.item_count ?? "—"} mono />
            <Stat label="Total claim" value={fmtAUD(job?.form_306?.total_aud ?? 0)} mono />
            <Stat label="Damage photos" value={job?.evidence?.total_damage_photos ?? "—"} mono />
            <Stat label="Completion photos" value={job?.evidence?.total_completion_photos ?? "—"} mono />
          </>
        )}
      </div>

      {isEPAR && (slipCount > 0 || cetCount > 0) && (
        <div className="card p-5 mb-6" data-testid="epar-assessment-form-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-heading text-lg font-semibold">EPAR Assessment Form (Form 1 / Form 2)</h3>
                {progress && progress.total > 0 && (
                  <span
                    data-testid="package-readiness-chip"
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] tracking-[0.14em] uppercase font-bold ${
                      progress.ready
                        ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                        : progress.complete
                          ? "bg-amber-100 text-amber-800 border border-amber-300"
                          : "bg-slate-200 text-slate-700 border border-slate-300"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${
                      progress.ready ? "bg-emerald-500"
                        : progress.complete ? "bg-amber-500"
                        : "bg-slate-500"
                    }`} />
                    {progress.ready
                      ? "Ready to ship"
                      : progress.complete
                        ? `Decided · ${progress.rfi} RFI`
                        : `${progress.decided} of ${progress.total} DMs verified`}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-600 max-w-2xl">
                Form 1 populated from the OneGMS application PDF + {slipCount > 0 ? `${slipCount} Slip Construction Estimate${slipCount===1?'':'s'}` : `${cetCount} CET workbook${cetCount===1?'':'s'}`}. Form 2 D/E/F auto-mirror Form 1 via native Excel formulas. Form 2 G + H pull from your per-DM decisions below.
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <a
                href={`${API}/ingest/jobs/${job.id}/epar-assessment-form`}
                className="btn btn-outline whitespace-nowrap"
                data-testid="download-epar-form-btn"
                download
              >
                Form 1+2 .xlsx
              </a>
              <a
                href={`${API}/ingest/jobs/${job.id}/verified-package`}
                className={`btn whitespace-nowrap ${progress && progress.ready ? "btn-primary ring-2 ring-emerald-400" : "btn-primary"}`}
                data-testid="download-verified-package-btn"
                download
              >
                Verified Package .zip
              </a>
            </div>
          </div>

          {/* Per-DM breakdown bar */}
          {progress && progress.total > 0 && (
            <div className="mt-4 flex items-center gap-3 text-[11px] tracking-[0.14em] uppercase font-semibold" data-testid="package-progress-bar">
              <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden flex">
                <div className="bg-emerald-500 h-full" style={{ width: `${(progress.yes / progress.total) * 100}%` }} />
                <div className="bg-amber-500 h-full" style={{ width: `${(progress.rfi / progress.total) * 100}%` }} />
                <div className="bg-red-500 h-full" style={{ width: `${(progress.no / progress.total) * 100}%` }} />
              </div>
              <div className="flex items-center gap-2 text-slate-600 text-[10.5px]" data-testid="package-progress-counts">
                <span className="text-emerald-700">{progress.yes} YES</span>
                <span className="text-amber-700">{progress.rfi} RFI</span>
                <span className="text-red-700">{progress.no} NO</span>
                {progress.undecided > 0 && <span className="text-slate-500">{progress.undecided} pending</span>}
              </div>
            </div>
          )}

          {/* Per-DM Form 2 G/H decision capture */}
          <DMDecisionsBlock job={job} onChange={load} />
        </div>
      )}

      {/* EW Finalisation — inline single-claim workflow */}
      {isEW && <EWFinalisationPanel job={job} />}

      {/* Sortable file tree of everything in the ingested ZIP — assessor
          can open the ledger / CET / form / photos in-place without
          leaving the app. */}
      <SourcePackageTree jobId={job.id} />

      <div className="card p-5 mb-6">
        <h3 className="font-heading text-lg font-semibold mb-3">Items needing your sorting ({(job.needs_review || []).filter(x => !x.resolved).length})</h3>
        {(job.needs_review || []).length === 0 ? (
          <div className="text-sm text-slate-500">No ambiguities — everything sorted automatically.</div>
        ) : (
          <div className="space-y-3">
            {(job.needs_review || []).map((it, i) => (
              <div key={`${i}-${it.kind || "nr"}`} className={`border rounded-md p-4 ${it.resolved ? "bg-emerald-50/40 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid={`review-item-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-[10.5px] tracking-[0.16em] uppercase font-bold text-slate-500 mb-1">{it.kind?.replace(/_/g," ")}</div>
                    <div className="text-sm text-slate-800 mb-1">{it.message}</div>
                    {it.items && it.items.length > 0 && (
                      <ul className="text-[12px] list-disc pl-5 text-slate-600 mt-2 space-y-0.5">
                        {it.items.map((x,k) => <li key={`${k}-${String(x).slice(0,24)}`} className="font-mono">{x}</li>)}
                      </ul>
                    )}
                    {it.resolved && (
                      <div className="text-[11px] text-emerald-700 mt-2">Resolved: {it.resolution}{it.resolution_value ? ` → ${it.resolution_value}` : ""}</div>
                    )}
                  </div>
                  {!it.resolved && (
                    <div className="flex flex-col gap-1.5 shrink-0" style={{minWidth:200}}>
                      {it.kind === "portal_classification" && (it.options || []).map(opt => (
                        <button key={opt} disabled={busy} onClick={()=>resolve(i, "set_portal", opt)} className="btn-secondary !py-1 !px-2 text-[11px]">{opt}</button>
                      ))}
                      <button disabled={busy} onClick={()=>resolve(i, "acknowledged")} className="btn-primary !py-1 !px-2 text-[11px]" data-testid={`resolve-ack-${i}`}>Acknowledge</button>
                      <button disabled={busy} onClick={()=>resolve(i, "flag_to_council")} className="btn-secondary !py-1 !px-2 text-[11px]">Flag to council</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {job.file_kinds && (
        <div className="card p-5 mb-6">
          <h3 className="font-heading text-base font-semibold mb-3">File classification</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(job.file_kinds).map(([k,v]) => (
              <div key={k} className="text-[12.5px] flex items-center justify-between border-b border-slate-100 py-1">
                <span className="capitalize text-slate-700">{k.replace(/_/g," ")}</span>
                <span className="font-mono tabular text-slate-500">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <VerifiedPackageTree jobId={job.id} />
    </div>
  );
}

